import { useEffect, useMemo, useState } from 'react';
import { Box, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { RxCross2 } from 'react-icons/rx';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import PasswordInput from 'components/Common/PasswordInput';
import Radio from 'components/Common/Radio';
import TelInput from 'components/Common/TelInput';
import { useUpdateTelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/hook';
import { TelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/types';
import SipTrunkingConfig from '@/pages/Business/Agent/Configure/DeployPhone/byoc/components/SipTrunkingConfig';
import { InboundSipTrunkResponse } from 'apis/business/agent/deployPhone/inboundSipTrunk/types';
import { OutboundSipTrunkResponse } from 'apis/business/agent/deployPhone/outBoundSipTrunk/types';
import { countryCodeFromPhoneNumber } from '@/utils/phoneNumberBilling';

type Props = {
  open: boolean;
  endpoint: TelephonyEndpoint | null;
  onClose: () => void;
};

type SipMode = 'registration' | 'trunking';

type SipFormValues = {
  number_type: string;
  phone_number: string;
  extension: string;
  main_line_number: string;
  friendly_name: string;
  username: string;
  password: string;
  authentication_username: string;
  proxy: string;
  outbound_proxy: string;
  realm: string;
  from_domain: string;
};

type TrunkingToggleValues = {
  inbound_configured: boolean;
  outbound_configured: boolean;
};

const helperSx = { fontSize: 12, color: '#6B7280', mt: 0.75, mb: 2.25, lineHeight: 1.45 };
const shrinkLabel = { shrink: true };

function configString(config: Record<string, unknown> | null | undefined, key: string) {
  const value = config?.[key];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function nestedConfig(config: Record<string, unknown> | null | undefined, key: string) {
  const value = config?.[key];
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/** String list for Byoc*SipTrunkForm (it wraps each entry as `{ value }`). */
function numbersFromConfig(config: Record<string, unknown> | null | undefined): string[] {
  const numbers = config?.numbers;
  if (!Array.isArray(numbers) || !numbers.length) return [''];
  return numbers.map((item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object' && 'value' in item) {
      return String((item as { value?: string }).value || '');
    }
    return '';
  });
}

function addressesFromConfig(config: Record<string, unknown> | null | undefined): string[] {
  const addresses = config?.allowed_addresses;
  if (!Array.isArray(addresses) || !addresses.length) return [''];
  return addresses.map((item) => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object' && 'value' in item) {
      return String((item as { value?: string }).value || '');
    }
    return '';
  });
}

function firstPhoneNumber(numbers?: unknown): string {
  if (!Array.isArray(numbers) || !numbers.length) return '';
  const first = numbers[0];
  if (typeof first === 'string') return first.trim();
  if (first && typeof first === 'object' && 'value' in first) {
    return String((first as { value?: string }).value || '').trim();
  }
  return '';
}

function resolveMode(endpoint: TelephonyEndpoint | null): SipMode {
  if (!endpoint) return 'registration';
  const type = String(endpoint.connection_config?.type || '');
  if (type === 'sip_trunking' || endpoint.kind === 'sip_trunk') return 'trunking';
  return 'registration';
}

function mapInboundFromEndpoint(endpoint: TelephonyEndpoint): InboundSipTrunkResponse {
  const config = endpoint.connection_config || {};
  const inbound = nestedConfig(config, 'inbound') || config;
  return {
    name: String(inbound.name || endpoint.friendly_name || ''),
    additional_config: {
      number_type: String(inbound.number_type || 'phone'),
      numbers: numbersFromConfig(inbound),
      allowed_addresses: addressesFromConfig(inbound),
      auth_username: String(inbound.auth_username || inbound.username || ''),
      auth_password: String(inbound.auth_password || inbound.password || ''),
      ringing_timeout: String(inbound.ringing_timeout || ''),
      media_encryption: String(inbound.media_encryption || ''),
      max_call_duration: String(inbound.max_call_duration || ''),
      type: 'sip_trunking'
    }
  } as InboundSipTrunkResponse;
}

function mapOutboundFromEndpoint(endpoint: TelephonyEndpoint): OutboundSipTrunkResponse {
  const config = endpoint.connection_config || {};
  const outbound = nestedConfig(config, 'outbound') || {};
  const transportRaw = outbound.transport;
  const transport =
    typeof transportRaw === 'number'
      ? transportRaw
      : transportRaw != null && String(transportRaw).trim() !== ''
        ? Number(transportRaw)
        : undefined;

  return {
    name: String(outbound.name || endpoint.friendly_name || ''),
    additional_config: {
      number_type: String(outbound.number_type || 'phone'),
      numbers: numbersFromConfig(outbound),
      address: String(outbound.address || ''),
      port: String(outbound.port || '5060'),
      ...(Number.isFinite(transport) ? { transport } : {}),
      auth_username: String(outbound.auth_username || outbound.username || ''),
      auth_password: String(outbound.auth_password || outbound.password || ''),
      type: 'sip_trunking'
    }
  };
}

export default function EditSipConnectionDrawer({ open, endpoint, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const mode = useMemo(() => resolveMode(endpoint), [endpoint]);
  const { mutateAsync: updateEndpoint, isPending } = useUpdateTelephonyEndpoint();

  const { control, reset, handleSubmit, watch } = useForm<SipFormValues>({
    defaultValues: {
      number_type: 'phone',
      phone_number: '',
      extension: '',
      main_line_number: '',
      friendly_name: '',
      username: '',
      password: '',
      authentication_username: '',
      proxy: '',
      outbound_proxy: '',
      realm: '',
      from_domain: ''
    }
  });

  const {
    control: trunkingControl,
    reset: resetTrunking,
    watch: watchTrunking
  } = useForm<TrunkingToggleValues>({
    defaultValues: { inbound_configured: true, outbound_configured: true }
  });

  const [inboundDraft, setInboundDraft] = useState<InboundSipTrunkResponse | null>(null);
  const [outboundDraft, setOutboundDraft] = useState<OutboundSipTrunkResponse | null>(null);
  const [inboundFormData, setInboundFormData] = useState<InboundSipTrunkResponse | undefined>(undefined);
  const [outboundFormData, setOutboundFormData] = useState<OutboundSipTrunkResponse | undefined>(undefined);

  const numberType = watch('number_type');
  const isExtension = numberType === 'extension';
  const inboundConfigured = watchTrunking('inbound_configured');
  const outboundConfigured = watchTrunking('outbound_configured');
  const canSaveTrunk =
    (inboundConfigured && Boolean(inboundDraft || inboundFormData)) ||
    (outboundConfigured && Boolean(outboundDraft || outboundFormData));

  useEffect(() => {
    if (!open || !endpoint) return;
    const config = endpoint.connection_config || {};

    if (resolveMode(endpoint) === 'registration') {
      const numberTypeValue =
        configString(config, 'number_type') || (endpoint.kind === 'extension' ? 'extension' : 'phone');
      reset({
        number_type: numberTypeValue,
        phone_number: numberTypeValue === 'extension' ? '' : endpoint.phone_number || '',
        extension: numberTypeValue === 'extension' ? endpoint.phone_number || '' : '',
        main_line_number: configString(config, 'main_line_number'),
        friendly_name: endpoint.friendly_name || '',
        username: configString(config, 'auth_username') || configString(config, 'username'),
        password: configString(config, 'auth_password') || configString(config, 'password'),
        authentication_username: configString(config, 'authentication_username'),
        proxy: configString(config, 'proxy'),
        outbound_proxy: configString(config, 'outbound_proxy'),
        realm: configString(config, 'realm'),
        from_domain: configString(config, 'from_domain')
      });
      return;
    }

    const inbound = nestedConfig(config, 'inbound');
    const outbound = nestedConfig(config, 'outbound');
    const hasInbound = Boolean(inbound);
    const hasOutbound = Boolean(outbound);
    resetTrunking({
      inbound_configured: hasInbound || !hasOutbound,
      outbound_configured: hasOutbound || !hasInbound
    });
    setInboundFormData(hasInbound ? mapInboundFromEndpoint(endpoint) : undefined);
    setOutboundFormData(hasOutbound ? mapOutboundFromEndpoint(endpoint) : undefined);
    setInboundDraft(hasInbound ? mapInboundFromEndpoint(endpoint) : null);
    setOutboundDraft(hasOutbound ? mapOutboundFromEndpoint(endpoint) : null);
  }, [open, endpoint, reset, resetTrunking]);

  useEffect(() => {
    if (!inboundConfigured) {
      setInboundDraft(null);
      setInboundFormData(undefined);
    }
  }, [inboundConfigured]);

  useEffect(() => {
    if (!outboundConfigured) {
      setOutboundDraft(null);
      setOutboundFormData(undefined);
    }
  }, [outboundConfigured]);

  const persistInbound = async (payload: InboundSipTrunkResponse) => {
    const phoneNumber = firstPhoneNumber(payload.additional_config?.numbers);
    if (!phoneNumber) throw new Error('Phone number is required');
    setInboundDraft(payload);
    setInboundFormData(payload);
  };

  const persistOutbound = async (payload: OutboundSipTrunkResponse) => {
    const phoneNumber = firstPhoneNumber(payload.additional_config?.numbers);
    if (!phoneNumber) throw new Error('Phone number is required');
    setOutboundDraft(payload);
    setOutboundFormData(payload);
  };

  const onSaveRegistration = handleSubmit(async (values) => {
    if (!endpoint) return;
    const kind = values.number_type === 'extension' ? 'extension' : 'sip_registration';
    const phoneNumber = values.number_type === 'extension' ? values.extension.trim() : values.phone_number.trim();
    const countrySource =
      values.number_type === 'extension' ? values.main_line_number.trim() : values.phone_number.trim();
    const countryCode = countryCodeFromPhoneNumber(countrySource, 'US');

    const connection_config: Record<string, unknown> = {
      ...(endpoint.connection_config || {}),
      type: 'sip_registration',
      number_type: values.number_type,
      username: values.username,
      auth_username: values.username,
      authentication_username: values.authentication_username,
      proxy: values.proxy,
      outbound_proxy: values.outbound_proxy || values.proxy,
      realm: values.realm,
      from_domain: values.from_domain
    };

    if (values.number_type === 'extension') {
      connection_config.main_line_number = values.main_line_number.trim();
    } else {
      delete connection_config.main_line_number;
    }

    if (values.password.trim()) {
      connection_config.password = values.password;
      connection_config.auth_password = values.password;
    }

    await updateEndpoint({
      id: endpoint.id,
      payload: {
        telephony_endpoint: {
          kind,
          provider: 'sip_connectivity',
          phone_number: phoneNumber,
          ...(countryCode ? { country_code: countryCode } : {}),
          friendly_name: values.friendly_name.trim() || undefined,
          connection_config
        }
      }
    });
    onClose();
  });

  const onSaveTrunking = async () => {
    if (!endpoint || !canSaveTrunk) return;

    const inbound = inboundConfigured ? inboundDraft || inboundFormData : null;
    const outbound = outboundConfigured ? outboundDraft || outboundFormData : null;
    const phoneNumber =
      firstPhoneNumber(inbound?.additional_config?.numbers) ||
      firstPhoneNumber(outbound?.additional_config?.numbers) ||
      endpoint.phone_number;
    if (!phoneNumber) return;

    const friendlyName = inbound?.name?.trim() || outbound?.name?.trim() || endpoint.friendly_name || undefined;
    // TelInput defaultCountry is US; incomplete +1 numbers must still send country_code.
    const countryCode = countryCodeFromPhoneNumber(phoneNumber, 'US') || endpoint.country_code || undefined;
    const existingFusion = endpoint.connection_config?.fusion;

    await updateEndpoint({
      id: endpoint.id,
      payload: {
        telephony_endpoint: {
          kind: 'sip_trunk',
          provider: 'sip_connectivity',
          phone_number: phoneNumber,
          ...(countryCode ? { country_code: countryCode } : {}),
          friendly_name: friendlyName,
          // Replace registration-shaped keys; only trunking inbound/outbound (+ fusion if any).
          connection_config: {
            type: 'sip_trunking',
            ...(existingFusion && typeof existingFusion === 'object' ? { fusion: existingFusion } : {}),
            ...(inbound
              ? {
                  inbound: {
                    name: inbound.name,
                    ...(inbound.additional_config || {})
                  }
                }
              : {}),
            ...(outbound
              ? {
                  outbound: {
                    name: outbound.name,
                    ...(outbound.additional_config || {})
                  }
                }
              : {})
          }
        }
      }
    });
    onClose();
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 720,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom='1px solid #E5E7EB'
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          Edit
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={2.5}>
        {mode === 'registration' ? (
          <Box>
            <Typography fontWeight={700} fontSize={15} color='#111827' mb={1.5}>
              SIP Registration
            </Typography>

            <Radio
              name='number_type'
              control={control}
              label=' '
              options={[
                { label: 'Phone', value: 'phone' },
                { label: 'Extension', value: 'extension' }
              ]}
            />

            {isExtension ? (
              <>
                <Input
                  name='extension'
                  control={control}
                  label='Extension'
                  placeholder='e.g. 121'
                  fullWidth
                  required
                  InputLabelProps={shrinkLabel}
                />
                <Typography sx={helperSx}>The extension this SIP account belongs to.</Typography>

                <TelInput
                  name='main_line_number'
                  control={control}
                  label='Main Line Number'
                  fullWidth
                  required
                  defaultCountry='US'
                  preferredCountries={['US', 'CA']}
                  InputLabelProps={shrinkLabel}
                />
                <Typography sx={helperSx}>Enter the mainline number corresponding to the above extension.</Typography>
              </>
            ) : (
              <>
                <TelInput
                  name='phone_number'
                  control={control}
                  label='Phone Number'
                  fullWidth
                  required
                  defaultCountry='CA'
                  preferredCountries={['CA', 'US']}
                  InputLabelProps={shrinkLabel}
                />
                <Typography sx={helperSx}>
                  The number this SIP account belongs to. Used as your caller ID when making calls.
                </Typography>
              </>
            )}

            <Input
              name='friendly_name'
              control={control}
              label='Friendly Name'
              fullWidth
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={helperSx}>Shown instead of the number in your lists. You can change it anytime.</Typography>

            <Input name='username' control={control} label='Username' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={helperSx}>
              Your SIP account login — this is what identifies your device to the server.
            </Typography>

            <PasswordInput name='password' control={control} label='Password' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={helperSx}>
              Prefills from the saved SIP password. Clear and leave blank on save to keep the existing value unchanged.
            </Typography>

            <Input
              name='authentication_username'
              control={control}
              label='Authentication Username'
              fullWidth
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={helperSx}>
              Only needed if your provider gives you a separate ID for authentication, different from your regular
              username.
            </Typography>

            <Input name='proxy' control={control} label='Proxy' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={helperSx}>
              The main SIP server your app connects and registers to. This is the only field required to get connected.
            </Typography>

            <Input
              name='outbound_proxy'
              control={control}
              label='Outbound Proxy'
              fullWidth
              InputLabelProps={shrinkLabel}
            />
            <Typography sx={helperSx}>
              Forces all outgoing calls through this server instead of the default route. Leave blank unless your
              provider specifically requires it.
            </Typography>

            <Input name='realm' control={control} label='Realm' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={helperSx}>
              Used only during login/authentication to verify your credentials. Usually same as your Proxy address —
              leave blank to auto-detect.
            </Typography>

            <Input name='from_domain' control={control} label='From Domain' fullWidth InputLabelProps={shrinkLabel} />
            <Typography sx={{ ...helperSx, mb: 0 }}>
              The domain shown to the other party when you make a call. Usually same as your Proxy address — leave blank
              to auto-detect.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography fontWeight={700} fontSize={15} color='#111827' mb={1.5}>
              SIP Trunking
            </Typography>
            <SipTrunkingConfig
              control={trunkingControl}
              inboundConfigured={inboundConfigured}
              outboundConfigured={outboundConfigured}
              inboundFormData={inboundFormData}
              outboundFormData={outboundFormData}
              selectedProvider='sip_connectivity'
              deleteInboundFormData={() => {
                setInboundDraft(null);
                setInboundFormData(undefined);
              }}
              deleteOutboundFormData={() => {
                setOutboundDraft(null);
                setOutboundFormData(undefined);
              }}
              persistInbound={persistInbound}
              persistOutbound={persistOutbound}
            />
          </>
        )}
      </Box>

      <Box
        display='flex'
        justifyContent='flex-end'
        gap={1.5}
        px={3}
        py={2.5}
        borderTop='1px solid #E5E7EB'
        bgcolor='#fff'
      >
        <Button
          variant='outlined'
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#E5E7EB',
            color: '#111827',
            fontWeight: 600,
            px: 2.5,
            bgcolor: '#fff',
            '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' }
          }}
        >
          Cancel
        </Button>
        {mode === 'registration' ? (
          <Button
            variant='contained'
            color='primary'
            onClick={onSaveRegistration}
            isPending={isPending}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, boxShadow: 'none', px: 2.5 }}
          >
            Save
          </Button>
        ) : (
          <Button
            variant='contained'
            color='primary'
            onClick={onSaveTrunking}
            isPending={isPending}
            disabled={!canSaveTrunk}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, boxShadow: 'none', px: 2.5 }}
          >
            Save
          </Button>
        )}
      </Box>
    </Drawer>
  );
}
